import { Box, Flex, Show } from "@chakra-ui/react";
import type { APIListing } from "models/types/api-listing";
import { AgencyLogo } from "components/agency-logo";
import { FeaturePreferencesConditionalComponent } from "components/feature-preferences/feature-preferences-conditional-component";
import { PropertyAttributes } from "components/property-banner-image/property-attributes";
import {
  getGreeting,
  PropertyBannerDetails,
} from "components/property-banner-image/property-banner-details";
import { useAuth } from "contexts/auth-provider";
import { WelcomePresentationModal } from "modules/vendor-portal/screens/overview/components/welcome-presentation/welcome-presentation-modal";
import { PropertyCategories } from "types/property";
import { getHighestResolutionImageSource } from "utils/images";

type VendorBannerImageProps = {
  listing?: APIListing;
  isLoading: boolean;
};

function isValidPropertyCategoryId(id: unknown): id is PropertyCategories {
  return Object.values(PropertyCategories).includes(id as PropertyCategories);
}

export function VendorBannerImage({
  listing,
  isLoading,
}: VendorBannerImageProps) {
  const { user, isVendor } = useAuth();
  const name =
    isVendor && user?.contact_name ? user.contact_name : user?.given_name;
  const currentHour = new Date().getHours();
  const greeting = getGreeting(currentHour);
  const backgroundImage = getHighestResolutionImageSource(
    listing?.marketing?.image,
  )?.url;

  return (
    <>
      <PropertyBannerDetails
        isLoading={isLoading}
        title={`${greeting}, ${name}!`}
        subtitle={listing?.address?.full_address}
        leftElement={
          <Flex
            columnGap={8}
            wrap={{ base: "wrap", xl: "nowrap" }}
            gap={{ base: 3, xl: 6 }}
          >
            <PropertyAttributes
              propertyDetails={listing?.property}
              propertyCategoryId={
                isValidPropertyCategoryId(listing?.category?.id)
                  ? (listing?.category?.id as PropertyCategories)
                  : PropertyCategories.Residential
              }
              advertisingPrice={listing?.price_advertise_as}
            />
            <FeaturePreferencesConditionalComponent
              feature={
                "vendor_portal.functionality.presentation_mode.show_vendor_presentation"
              }
            >
              <WelcomePresentationModal
                listing={listing}
                hasBackgroundImage={!!backgroundImage}
              />
            </FeaturePreferencesConditionalComponent>
          </Flex>
        }
        backgroundImage={backgroundImage}
        rightElement={
          <Show above={"md"}>
            <Box flex={1} display={"flex"} justifyContent={"flex-end"}>
              <AgencyLogo
                imageProps={{ objectFit: "contain", alignSelf: "flex-end" }}
              />
            </Box>
          </Show>
        }
      />
    </>
  );
}
